import { Search, UserRound, X } from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Callout, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { ApiError } from "@/lib/api";
import { contactName, stageText } from "@/lib/contacts";
import { useContacts, useGreetingFields, useGreetingPreview } from "@/lib/queries";
import type { ContactRow, GreetingPreview } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";
import { cn, formatDateTime, formatPhoneNumber } from "@/lib/utils";
import {
  fieldPhrase, kindOf, languageText, missingText, scriptProblem, versionLine,
} from "@/lib/voicemail";

/**
 * A voicemail greeting as one lead would hear it (ADR-112 in `platform`).
 *
 * A script is text with fields, and core-api is the one that fills it: which values a lead has,
 * how a date or a number is said aloud in the greeting's language, and which greeting the library
 * picks for a lead are all decided there (`POST /voicemail-messages/preview`). This file only asks
 * and shows the answer, in two places: under a draft in the library's form, so the words are read
 * before they are saved, and from the library's page, for "what would this lead hear?".
 *
 * The lead is searched on the server (`GET /leads`), because a tenant's contacts are tens of
 * thousands. The rep the greeting names is whoever is reading it: an admin sees their own name
 * where a rep's will be.
 */

/** A lead picked for a preview: what the request needs, and what the reader reads. */
export interface PreviewLead {
  id: string;
  name: string;
  phone: string;
  stage: string | null;
  language: string | null;
}

function asPreviewLead(row: ContactRow): PreviewLead {
  return {
    id: row.id,
    name: contactName(row) ?? "No name",
    phone: row.phone,
    stage: row.stage,
    language: row.language,
  };
}

/** How many contacts a search lists. A preview needs one; a longer list is a search to narrow. */
const SHOWN = 6;

/**
 * Pick the lead to read a greeting for: a search box, and the first few contacts it finds. Once one
 * is picked it is a line with a way to change it. Expands in place, as the pickers in a drawer do,
 * because a drawer's body scrolls and a popover inside it would be clipped.
 */
export function PreviewLeadPicker({
  value, onChange, id,
}: {
  value: PreviewLead | null;
  onChange: (lead: PreviewLead | null) => void;
  id?: string;
}) {
  const [search, setSearch] = React.useState("");
  const debounced = useDebounced(search.trim(), 250);
  const searching = value === null && debounced.length >= 2;
  const { data, isFetching, error } = useContacts({ q: debounced }, 0, searching);
  const rows = searching ? (data?.items ?? []).slice(0, SHOWN) : [];

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5">
        <UserRound className="size-4 shrink-0 text-text-subtle" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{value.name}</p>
          <p className="truncate text-xs text-text-muted">
            {stageText(value.stage).label} · {languageText(value.language) ?? "No language: read as English"}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon-sm" aria-label="Pick another lead" onClick={() => onChange(null)}>
          <X />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Input
        id={id}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search a contact by name or phone"
        autoComplete="off"
        leading={<Search className="size-3.5" aria-hidden />}
        trailing={isFetching && searching ? <Spinner className="size-3.5" /> : undefined}
      />
      {error && searching && (
        <p className="text-xs text-danger-11">{error instanceof ApiError ? error.message : "Could not search contacts."}</p>
      )}
      {searching && !isFetching && !error && rows.length === 0 && (
        <p className="text-xs text-text-muted">No contact matches.</p>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-md border border-border" aria-label="Contacts found">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                onClick={() => {
                  onChange(asPreviewLead(row));
                  setSearch("");
                }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-text">{contactName(row) ?? "No name"}</span>
                  <span className="block truncate text-xs text-text-muted">
                    {formatPhoneNumber(row.phone)} · {stageText(row.stage).label}
                  </span>
                </span>
                {row.language && (
                  <Badge tone="outline" size="sm">
                    {languageText(row.language)}
                  </Badge>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The words of a greeting, set apart as what will be said. */
function Spoken({ text, stale }: { text: string; stale?: boolean }) {
  return (
    <blockquote
      className={cn(
        "rounded-md border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-text",
        stale && "opacity-60",
      )}
    >
      {text}
    </blockquote>
  );
}

function useFieldLabels(enabled: boolean): { labels: Map<string, string>; known: Set<string> | undefined } {
  const { data } = useGreetingFields(enabled);
  return React.useMemo(
    () => ({
      labels: new Map((data ?? []).map((f) => [f.name, fieldPhrase(f.label)])),
      known: data ? new Set(data.map((f) => f.name)) : undefined,
    }),
    [data],
  );
}

/**
 * Under a draft in the form: the draft filled for a lead the admin picks. It asks again a moment
 * after the typing stops, keeps the last words on screen meanwhile, and does not ask at all for a
 * draft the form already refuses, since core-api would only refuse it too.
 */
export function DraftPreview({ script, language }: { script: string; language: string }) {
  const [lead, setLead] = React.useState<PreviewLead | null>(null);
  const draft = useDebounced(script, 400);
  const { labels, known } = useFieldLabels(true);
  const problem = scriptProblem(draft, known);
  const asked = lead && problem === null ? { lead_id: lead.id, script: draft, language: language || undefined } : null;
  const { data, error, isFetching, isPlaceholderData } = useGreetingPreview(asked);
  const missing = data ? missingText(data.missing, labels) : null;

  return (
    <div className="space-y-2">
      <PreviewLeadPicker value={lead} onChange={setLead} id="voicemail-preview-lead" />
      {lead && problem !== null && (
        <p className="text-xs text-text-muted">The preview waits for a greeting that can be saved.</p>
      )}
      {asked && error && (
        <Callout tone="danger">{error instanceof ApiError ? error.message : "Could not read the preview."}</Callout>
      )}
      {asked && data && !error && (
        <>
          <Spoken text={data.text ?? ""} stale={isFetching || isPlaceholderData} />
          {missing && <Callout tone="warning">{missing}</Callout>}
          <p className="text-xs text-text-muted">
            Values are written the way they will be spoken, in {languageText(data.language) ?? data.language}. The rep's
            name is yours here; on a call it is the rep's.
          </p>
        </>
      )}
    </div>
  );
}

/** What the library picked for a lead, in words. */
function Picked({ preview }: { preview: GreetingPreview }) {
  const { labels } = useFieldLabels(true);
  const message = preview.message;
  // Which of a greeting's two versions this lead hears now, and why (ADR-116 in `platform`).
  const version = versionLine(preview, formatDateTime);
  return (
    <div className="space-y-3">
      {message === null ? (
        <Callout tone="warning">
          Nothing in the library fits this lead: no message for {stageText(preview.stage).label} in{" "}
          {languageText(preview.language) ?? preview.language}, and no general one. A rep who reaches their voicemail
          speaks the message.
        </Callout>
      ) : (
        <div className="space-y-1.5">
          <p className="flex flex-wrap items-center gap-1.5 text-sm text-text">
            <span className="font-medium">{message.name}</span>
            <Badge tone="outline" size="sm">
              {kindOf(message) === "script" ? "Greeting text" : "Recording"}
            </Badge>
            {message.language && (
              <Badge tone="outline" size="sm">
                {languageText(message.language)}
              </Badge>
            )}
            {version && (
              <Badge tone={preview.version === "b" ? "warning" : "outline"} size="sm">
                Version {preview.version === "b" ? "B" : "A"}
              </Badge>
            )}
          </p>
          {preview.text !== null ? (
            <>
              <Spoken text={preview.text} />
              {version && <p className="text-xs text-text-muted">{version}</p>}
            </>
          ) : (
            <p className="text-xs text-text-muted">A recording: it plays the same words to every lead.</p>
          )}
        </div>
      )}
      {preview.skipped.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-text">Passed over for this lead</p>
          <ul className="space-y-1 text-xs text-text-muted">
            {preview.skipped.map((s) => (
              <li key={s.slug}>
                <span className="text-text">{s.name}</span>: {missingText(s.missing, labels)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * From the library's page: pick a lead and read what their voicemail would get, by their stage and
 * language, with the greetings that were passed over and why.
 */
export function LeadGreetingDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [lead, setLead] = React.useState<PreviewLead | null>(null);
  const { data, error, isPending } = useGreetingPreview(open && lead ? { lead_id: lead.id } : null);

  React.useEffect(() => {
    if (!open) setLead(null);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Preview for a lead</DialogTitle>
          <DialogDescription>
            The message the library picks for a lead by their stage and language, with their own details filled in.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <PreviewLeadPicker value={lead} onChange={setLead} id="voicemail-lead-preview" />
          {lead && isPending && !error && <Spinner />}
          {lead && error && (
            <Callout tone="danger">{error instanceof ApiError ? error.message : "Could not read the preview."}</Callout>
          )}
          {lead && data && !error && <Picked preview={data} />}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
